import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { ExhibitDetailAttribute } from '../../firestore-models/exhibit-detail-attribute';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { ApiBaseAttribute, ApiSetExhibitDetailAttributesRequest } from '../../api-client-module';

import { ApiClientService } from '../../api-client.service';
import { ConfigurationService } from '../../configuration.service';
import { SpinnerService } from '../../services/spinner.service';
import { AppConst } from '../../app-const';

import { ExhibitDetailAttributeDialogComponent } from './exhibit-detail-attribute-dialog/exhibit-detail-attribute-dialog.component';
import { MessageDialogComponent } from '../../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-exhibit-detail-attribute',
  templateUrl: './exhibit-detail-attribute.component.html',
  styleUrls: ['./exhibit-detail-attribute.component.css'],
  standalone: false,
})
export class ExhibitDetailAttributeComponent implements OnInit, OnDestroy {

  //ExhibitDetail Attributeコレクション
  private exhibitDetailAttributesCollection!: AngularFirestoreCollection<ExhibitDetailAttribute>;
  exhibitDetailAttributesObservable!: Observable<ExhibitDetailAttribute[]>;
  exhibitDetailAttributesSubscribe!: Subscription;
  //各属性情報に紐付くプロジェクトID
  projectId: string;
  //各属性情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  //属性情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'attr_id', 'name'];
  dataSource = new MatTableDataSource<ExhibitDetailAttribute>();
  selection = new SelectionModel<ExhibitDetailAttribute>(true, []);

  // ユーザータイプ
  readonly USER_TYPE_USER: number = AppConst.MANAGE_USER_TYPE_USER;
  userType!: number;
  isEdit: boolean = false;

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.userType = this.configurationService.getUserType();
    this.isEdit = this.configurationService.getIsEdit();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.exhibitDetailAttributesCollection = this.angularFirestore.collection<ExhibitDetailAttribute>(
      AppConst.FIRESTORE_EXHIBIT_DETAIL_ATTR, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.exhibitDetailAttributesObservable = this.exhibitDetailAttributesCollection.valueChanges({ idField: 'id' });
    this.exhibitDetailAttributesSubscribe = this.exhibitDetailAttributesObservable.subscribe(exhibitDetailAttributes => {
      this.dataSource.data = this.addFilterValue(exhibitDetailAttributes);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.exhibitDetailAttributesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: ExhibitDetailAttribute): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(exhibitDetailAttribute: ExhibitDetailAttribute): void {
    let apiExhibitDetailAttribute: ApiBaseAttribute = {
      attribute_id: exhibitDetailAttribute.attribute_id,
      attribute_names: exhibitDetailAttribute.attribute_names,
      project_id: exhibitDetailAttribute.project_id,
      enabled: exhibitDetailAttribute.enabled
    };
    const dialogRef = this.dialog.open(ExhibitDetailAttributeDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, exhibitDetailAttribute: apiExhibitDetailAttribute }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(ExhibitDetailAttributeDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, exhibitDetailAttribute: null }
    });
  }
  
  //削除
  deleteData(): void{
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedExhibitDetailAttributes: Array<ApiBaseAttribute> = new Array<ApiBaseAttribute>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedExhibitDetailAttribute: ApiBaseAttribute = {
            attribute_id: row.attribute_id,
            attribute_names: row.attribute_names,
            project_id: row.project_id,
            enabled: row.enabled
          };
          selectedExhibitDetailAttributes.push(selectedExhibitDetailAttribute);
        }
      }
      if (!!result) {
        let request: ApiSetExhibitDetailAttributesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          attributes: selectedExhibitDetailAttributes
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setExhibitDetailAttributes(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
  
    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'attr_id':
          return compare(a.attribute_id, b.attribute_id, isAsc);
        case 'name':
          const aName = a.attribute_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.attribute_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.attribute_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
